import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  INPUTS_QUERY_KEY,
  INPUT_BY_ID_QUERY_KEY,
} from 'constants/queryKeys';
import { IInput } from 'interfaces/IInput';
import InputService from 'services/InputService';
import BrowserStorageUtils from 'utils/BrowserStorageUtils';

interface UseDeleteInputArgs {
  activeOrganisationId: string | null;
}

/**
 * Deletes a paddock plan and drops it from the org's inputs cache, so the tree
 * loses the node without a refetch. Any locally stored draft for that document
 * is cleared too — it can never be saved once the plan is gone. Redux
 * selection is left to the caller, matching useCreateInput.
 */
export const useDeleteInput = ({
  activeOrganisationId,
}: UseDeleteInputArgs) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (inputId: string) => {
      const clientNow = new Date().toISOString();
      return InputService.Delete(inputId, clientNow);
    },
    onSuccess: (_response, inputId) => {
      BrowserStorageUtils.removeEditedInput(inputId);
      BrowserStorageUtils.removeOriginalInput(inputId);

      // Drop the single-document cache too, or a Preview tab already open on
      // this plan keeps rendering it.
      queryClient.removeQueries({
        queryKey: [INPUT_BY_ID_QUERY_KEY, inputId],
      });

      if (!activeOrganisationId) return;

      queryClient.setQueryData<IInput[]>(
        [INPUTS_QUERY_KEY, activeOrganisationId],
        (current) => {
          if (!current) return current;
          return current.filter((item) => item.id !== inputId);
        }
      );

      // The list has a 5-minute staleTime, so a GetByOrgActive already in flight
      // could otherwise overwrite the filtered cache and bring the node back.
      queryClient.invalidateQueries({
        queryKey: [INPUTS_QUERY_KEY, activeOrganisationId],
      });
    },
  });
};

export default useDeleteInput;
